import React, { useState, useEffect } from 'react';
import { getSymptoms, createSymptom, updateSymptom, deleteSymptom } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, Calendar, Edit2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useReadOnly } from '../context/ReadOnlyContext';
import { formatDisplayDate } from '../utils/dateUtils';
import useUsageLimit from '../hooks/useUsageLimit';
import SoftBlurWrapper from '../components/SoftBlurWrapper';
import MoodCheckIn from '../components/MoodCheckIn';

const SymptomsPage = () => {
  const { user } = useAuth();
  const { isReadOnly, targetUser, readonlyToken } = useReadOnly();
  const displayUser = isReadOnly ? targetUser : user;
  const userCountry = displayUser?.country || 'AU';
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Usage limit hook for symptom gating
  const { isSymptomLimitReached, canAddSymptom, refetch: refetchUsage } = useUsageLimit();
  
  // Create state
  const [isCreating, setIsCreating] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    symptoms: [],
    otherSymptom: '',
    notes: ''
  });
  
  // Edit state - NO formKey, simple boolean
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    symptoms: [],
    otherSymptom: '',
    notes: ''
  });

  const commonSymptoms = [
    // Classic B-Symptoms & Common CLL/SLL
    'Fatigue',
    'Night Sweats',
    'Fever',
    'Chills',
    'Weight Loss',
    'Swollen Lymph Nodes',
    'Easy Bruising',
    'Frequent Infections',
    // Physical Symptoms
    'Shortness of Breath',
    'Abdominal Fullness',
    'Dizziness',
    'Bone or Joint Pain',
    'Nosebleeds',
    'Bleeding Gums',
    'Itchy Skin',
    'Pale Skin',
    'Headaches',
    'Muscle Weakness',
    'Sore Throat',
    // Treatment-Related
    'Tingling or numbness (hands/feet)',
    'Mouth Sores',
    'Nausea',
    'Skin Rash',
    // General Wellbeing
    'Brain Fog',
    'Sleep Problems',
    'Loss of Appetite',
    'Anxiety',
    'Depression'
  ];

  useEffect(() => {
    fetchSymptoms();
  }, [readonlyToken]); // Re-fetch when entering/exiting readonly mode

  const fetchSymptoms = async () => {
    try {
      const data = await getSymptoms();
      setSymptoms(data);
    } catch (error) {
      toast.error('Failed to load symptoms');
    } finally {
      setLoading(false);
    }
  };

  // TOP-LEVEL CREATE HANDLER
  const handleCreate = () => {
    setCreateFormData({
      date: new Date().toISOString().split('T')[0],
      symptoms: [],
      otherSymptom: '',
      notes: ''
    });
    setIsCreating(true);
  };

  // TOP-LEVEL SAVE CREATE HANDLER
  const handleSaveCreate = async (e) => {
    e.preventDefault();
    
    // Build symptom list
    let symptomList = [...createFormData.symptoms];
    if (createFormData.otherSymptom.trim()) {
      symptomList.push(createFormData.otherSymptom.trim());
    }

    if (symptomList.length === 0) {
      toast.error('Please select at least one symptom or enter other symptom');
      return;
    }

    const payload = {
      date: createFormData.date,
      symptoms: symptomList,
      notes: createFormData.notes || null
    };

    try {
      await createSymptom(payload);
      toast.success('Symptom entry added');
      setIsCreating(false);
      await fetchSymptoms();
      await refetchUsage(); // Refetch usage to update limit status
      setCreateFormData({
        date: '',
        symptoms: [],
        otherSymptom: '',
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to add symptom entry');
    }
  };

  // TOP-LEVEL CANCEL CREATE HANDLER
  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateFormData({
      date: '',
      symptoms: [],
      otherSymptom: '',
      notes: ''
    });
  };

  // TOP-LEVEL CREATE SYMPTOM TOGGLE HANDLER
  const handleCreateSymptomToggle = (symptom) => {
    const currentSymptoms = createFormData.symptoms;
    if (currentSymptoms.includes(symptom)) {
      setCreateFormData({
        ...createFormData,
        symptoms: currentSymptoms.filter(s => s !== symptom)
      });
    } else {
      setCreateFormData({
        ...createFormData,
        symptoms: [...currentSymptoms, symptom]
      });
    }
  };

  // TOP-LEVEL EDIT HANDLER - No effects, no reactive deps
  const handleEdit = async (symptomEntry) => {
    console.log('🔵 Symptoms edit clicked for:', symptomEntry.id);
    
    try {
      // Fetch fresh data from backend
      const freshData = await getSymptoms();
      const freshRecord = freshData.find(s => s.id === symptomEntry.id);
      
      if (!freshRecord) {
        toast.error('Record not found');
        return;
      }
      
      // Set editing state
      setEditingId(freshRecord.id);
      setEditFormData({
        date: freshRecord.date,
        symptoms: freshRecord.symptoms || [],
        otherSymptom: '',
        notes: freshRecord.notes || ''
      });
      setIsEditing(true);
      
      console.log('✅ Symptoms edit state set successfully');
    } catch (error) {
      console.error('Symptoms edit failed:', error);
      toast.error('Failed to load record for editing');
    }
  };

  // TOP-LEVEL SAVE HANDLER
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    console.log('💾 Saving symptoms edit for:', editingId);
    
    // Build symptom list
    let symptomList = [...editFormData.symptoms];
    if (editFormData.otherSymptom.trim()) {
      symptomList.push(editFormData.otherSymptom.trim());
    }

    if (symptomList.length === 0) {
      toast.error('Please select at least one symptom or enter other symptom');
      return;
    }

    const payload = {
      date: editFormData.date,
      symptoms: symptomList,
      notes: editFormData.notes || null
    };

    try {
      // HTTP PUT request
      await updateSymptom(editingId, payload);
      console.log('✅ Symptoms save successful');
      toast.success('Symptom entry updated');
      
      // Close modal
      setIsEditing(false);
      
      // Explicit refetch
      console.log('🔄 Refetching symptoms...');
      await fetchSymptoms();
      
      // Reset state explicitly
      setEditingId(null);
      setEditFormData({
        date: '',
        symptoms: [],
        otherSymptom: '',
        notes: ''
      });
      
      console.log('✅ Symptoms edit cycle complete');
    } catch (error) {
      console.error('Symptoms save failed:', error);
      toast.error('Failed to update symptom entry');
    }
  };

  // TOP-LEVEL CANCEL HANDLER
  const handleCancelEdit = () => {
    console.log('❌ Symptoms edit cancelled');
    setIsEditing(false);
    setEditingId(null);
    setEditFormData({
      date: '',
      symptoms: [],
      otherSymptom: '',
      notes: ''
    });
  };

  const handleSymptomToggle = (symptom) => {
    const currentSymptoms = editFormData.symptoms;
    if (currentSymptoms.includes(symptom)) {
      setEditFormData({
        ...editFormData,
        symptoms: currentSymptoms.filter(s => s !== symptom)
      });
    } else {
      setEditFormData({
        ...editFormData,
        symptoms: [...currentSymptoms, symptom]
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this symptom entry?')) return;
    
    try {
      await deleteSymptom(id);
      toast.success('Symptom entry deleted');
      await fetchSymptoms();
      await refetchUsage(); // Refetch usage to update limit status
    } catch (error) {
      toast.error('Failed to delete symptom entry');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading symptoms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">
            Symptoms
          </h1>
          <p className="text-lg leading-relaxed text-slate-700">Track symptoms over time</p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 text-sm sm:text-base"
          disabled={!canAddSymptom}
          title={!canAddSymptom ? 'Symptom limit reached - upgrade to add more' : ''}
        >
          <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
          Add New
        </Button>
      </div>

      {/* Daily Check-in Section */}
      <MoodCheckIn compact={true} />

      {/* CREATE MODAL - NO key prop */}
      {isCreating && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] flex flex-col">
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-800">Log Symptoms</h2>
              <button
                onClick={handleCancelCreate}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 pr-5">
              <form id="create-symptom-form" onSubmit={handleSaveCreate} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                  <input
                    type="date"
                    value={createFormData.date}
                    onChange={(e) => setCreateFormData({ ...createFormData, date: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">
                    Select Symptoms
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {commonSymptoms.map((symptom) => (
                      <label
                        key={symptom}
                        className={`block p-2 border rounded-lg cursor-pointer text-[11px] ${
                          createFormData.symptoms.includes(symptom)
                            ? 'bg-blue-50 border-blue-300 text-blue-900'
                            : 'bg-white border-slate-200 text-slate-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={createFormData.symptoms.includes(symptom)}
                          onChange={() => handleCreateSymptomToggle(symptom)}
                          className="h-3.5 w-3.5 mr-1.5 align-middle"
                        />
                        {symptom}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Other Symptom
                  </label>
                  <input
                    type="text"
                    value={createFormData.otherSymptom}
                    onChange={(e) => setCreateFormData({ ...createFormData, otherSymptom: e.target.value })}
                    placeholder="Enter other symptom"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={createFormData.notes}
                    onChange={(e) => setCreateFormData({ ...createFormData, notes: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none"
                    placeholder="Additional details..."
                  />
                </div>

                {/* Buttons at bottom of content */}
                <div className="flex gap-2 pt-4">
                  <button type="button" onClick={handleCancelCreate} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL - NO key prop */}
      {isEditing && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] flex flex-col">
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-800">Edit Symptoms</h2>
              <button
                onClick={handleCancelEdit}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 pr-5">
              <form id="edit-symptom-form" onSubmit={handleSaveEdit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                  <input
                    type="date"
                    value={editFormData.date}
                    onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">
                    Select Symptoms
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {commonSymptoms.map((symptom) => (
                      <label
                        key={symptom}
                        className={`block p-2 border rounded-lg cursor-pointer text-[11px] ${
                          editFormData.symptoms.includes(symptom)
                            ? 'bg-blue-50 border-blue-300 text-blue-900'
                            : 'bg-white border-slate-200 text-slate-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={editFormData.symptoms.includes(symptom)}
                          onChange={() => handleSymptomToggle(symptom)}
                          className="h-3.5 w-3.5 mr-1.5 align-middle"
                        />
                        {symptom}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Other Symptom
                  </label>
                  <input
                    type="text"
                    value={editFormData.otherSymptom}
                    onChange={(e) => setEditFormData({ ...editFormData, otherSymptom: e.target.value })}
                    placeholder="Enter other symptom"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none"
                    placeholder="Additional details..."
                  />
                </div>

                {/* Buttons at bottom of content */}
                <div className="flex gap-2 pt-4">
                  <button type="button" onClick={handleCancelEdit} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Symptoms List - Wrapped with SoftBlur when limit reached */}
      <SoftBlurWrapper isBlurred={isSymptomLimitReached}>
        <div className="space-y-4">
          {symptoms.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 border border-slate-100 shadow-sm text-center">
              <Calendar className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No symptoms tracked yet</h3>
              <p className="text-slate-600">Start tracking by adding your first symptom entry</p>
            </div>
          ) : (
            symptoms.map((symptomEntry) => (
              <div
                key={symptomEntry.id}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-slate-900 mb-2">{formatDisplayDate(symptomEntry.date, userCountry)}</p>
                    <div className="flex flex-wrap gap-2">
                      {symptomEntry.symptoms.map((symptom, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                        >
                          {symptom}
                        </span>
                      ))}
                    </div>
                    {symptomEntry.notes && (
                      <div className="mt-3 bg-slate-50 rounded-lg p-3">
                        <p className="text-sm text-slate-700">{symptomEntry.notes}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {/* EDIT BUTTON - Symptoms ONLY */}
                    <button
                      onClick={() => handleEdit(symptomEntry)}
                      data-testid={`edit-symptom-${symptomEntry.id}`}
                      className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(symptomEntry.id)}
                      data-testid={`delete-symptom-${symptomEntry.id}`}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </SoftBlurWrapper>
    </div>
  );
};

export default SymptomsPage;
